import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import html2pdf from 'html2pdf.js';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Paper, IconButton, FormControl, Select, MenuItem, InputLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, TextField, Alert, CircularProgress, TablePagination
} from '@mui/material';
import { Edit, Delete, PictureAsPdf } from '@mui/icons-material';

// Import your logo here — adjust the path as necessary
import schoolLogo from './logo.jpg';

const api = axios.create({ baseURL: 'https://www.janalwilliamsedufoundation.online/' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('access');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const dayOptions = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
];

const periodOptions = [
  { value: 1, label: '1st Period' },
  { value: 2, label: '2nd Period' },
  { value: 3, label: '3rd Period' },
  { value: 99, label: 'Recess Period' },
  { value: 4, label: '4th Period' },
  { value: 5, label: '5th Period' },
  { value: 6, label: '6th Period' },
  { value: 7, label: '7th Period' },
];

const formatTo12Hour = (timeStr) => {
  if (!timeStr) return '';
  const [hour, minute] = timeStr.split(':');
  const date = new Date();
  date.setHours(parseInt(hour, 10));
  date.setMinutes(parseInt(minute, 10));
  return date.toLocaleTimeString([], {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const rowColors = ['#f0f8ff', '#fafad2', '#e6f2ff', '#ffe4e1', '#e0ffff'];

const SchedulePage = () => {
  const [gradeClasses, setGradeClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [division, setDivision] = useState('');
  const [schedules, setSchedules] = useState([]);
  const [alert, setAlert] = useState({ type: '', msg: '' });
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(4);
  const pdfRef = useRef();

  useEffect(() => {
    api.get('api/users/grade-classes/').then(res => setGradeClasses(res.data));
    fetchSchedules();
  }, []);

  const fetchSchedules = async (gcId = '') => {
    const url = gcId ? `class-schedules/by-class/?grade_class_id=${gcId}` : 'class-schedules/';
    const res = await api.get(url);
    setSchedules(res.data);
    setPage(0);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete?',
      text: 'Schedule will be permanently deleted.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!'
    });
    if (confirm.isConfirmed) {
      try {
        await api.delete(`class-schedules/${id}/`);
        setAlert({ type: 'success', msg: 'Schedule deleted.' });
        fetchSchedules(selectedClass);
      } catch {
        setAlert({ type: 'error', msg: 'Failed to delete schedule.' });
      }
    }
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const submitEdit = async () => {
    setLoading(true);
    await new Promise(res => setTimeout(res, 3000));
    try {
      await api.patch(`class-schedules/${editItem.id}/`, editForm);
      setLoading(false);
      setEditItem(null);
      fetchSchedules(selectedClass);

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Schedule updated successfully!',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch {
      setLoading(false);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update schedule.',
        timer: 3000,
        showConfirmButton: true,
      });
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const getDivisionGrades = (div) => {
    switch (div) {
      case 'Elementary': return ['4th Grade', '5th Grade', '6th Grade'];
      case 'Junior High': return ['7th Grade', '8th Grade', '9th Grade'];
      case 'Senior High': return ['10th Grade', '11th Grade', '12th Grade'];
      default: return [];
    }
  };

  const filteredByDivision = schedules.filter(s =>
    getDivisionGrades(division).includes(s.grade_class.name)
  );

  const downloadPDF = () => {
    if (!division) return;
    const element = pdfRef.current;
    const opt = {
      margin: 0.3,
      filename: `${division}_Division_Schedule.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' },
    };
    html2pdf().set(opt).from(element).save();
  };

  const paginatedSchedules =
    schedules.length > 4 ? schedules.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : schedules;

  return (
    <div>
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>All Class Schedules</Typography>
        {alert.msg && (
          <Alert
            sx={{ mb: 2, bgcolor: alert.type === 'error' ? 'error.main' : 'info.light' }}
            severity={alert.type}
          >
            {alert.msg}
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Class</InputLabel>
            <Select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                fetchSchedules(e.target.value);
              }}
            >
              <MenuItem value=''>All</MenuItem>
              {gradeClasses.map(gc => (
                <MenuItem key={gc.id} value={gc.id}>{gc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Select Division</InputLabel>
            <Select value={division} onChange={(e) => setDivision(e.target.value)}>
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Elementary">Elementary (4th–6th)</MenuItem>
              <MenuItem value="Junior High">Junior High (7th–9th)</MenuItem>
              <MenuItem value="Senior High">Senior High (10th–12th)</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<PictureAsPdf />}
            onClick={downloadPDF}
            disabled={!division}
          >
            Download PDF
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#0D47A1' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Period</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Grade Class</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Day</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Subject</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Teacher</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Time</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedSchedules.map((s, index) => (
                <TableRow
                  key={s.id}
                  sx={{
                    bgcolor: rowColors[index % rowColors.length],
                    '&:hover': { bgcolor: '#bbdefb' },
                  }}
                >
                  <TableCell>{s.period_label}</TableCell>
                  <TableCell>{s.grade_class.name}</TableCell>
                  <TableCell>{s.day_label}</TableCell>
                  <TableCell>{s.subject.name}</TableCell>
                  <TableCell>{s.teacher.full_name}</TableCell>
                  <TableCell>{formatTo12Hour(s.start_time)} - {formatTo12Hour(s.end_time)}</TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => {
                      setEditItem(s);
                      setEditForm({
                        grade_class_id: s.grade_class.id,
                        subject_id: s.subject.id,
                        teacher_id: s.teacher.id,
                        day_of_week: s.day_of_week,
                        period: s.period,
                        start_time: s.start_time,
                        end_time: s.end_time
                      });
                    }}>
                      <Edit />
                    </IconButton>
                    <IconButton onClick={() => handleDelete(s.id)}>
                      <Delete color="error" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {schedules.length > 4 && (
            <TablePagination
              component="div"
              count={schedules.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[]}
            />
          )}
        </TableContainer>

        {/* PDF export hidden modern layout */}
        <div style={{ display: 'none' }}>
          <div ref={pdfRef} style={{ padding: 20, width: 1000, fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
              <img src={schoolLogo} alt="School Logo" style={{ width: 80, height: 80, marginRight: 15, objectFit: 'contain' }} />
              <div>
                <h2 style={{ margin: 0, fontSize: 24, color: '#0D47A1' }}>Jana L. Williams Educational Foundation</h2>
                <p style={{ margin: 0, fontSize: 14, color: '#555' }}>{division} Division Class Schedule</p>
              </div>
            </div>

            {/* Header Row */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(6, 1fr)',
              backgroundColor: '#e3f2fd',
              color: '#0D47A1',
              fontWeight: '700',
              padding: 10,
              borderRadius: '8px 8px 0 0',
              textAlign: 'center',
              userSelect: 'none',
              fontSize: 14,
            }}>
              {['Period', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map(day => (
                <div key={day}>{day}</div>
              ))}
            </div>

            {/* Period Rows */}
            {[1, 2, 3, 99, 4, 5, 6, 7].map(period => {
              const periodLabel = periodOptions.find(p => p.value === period)?.label || '';
              return (
                <div
                  key={period}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    backgroundColor: period % 2 === 0 ? '#f1f8e9' : '#fff3e0',
                    padding: 10,
                    borderBottom: '1px solid #ccc',
                    fontSize: 13,
                    textAlign: 'center',
                    minHeight: 70,
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontWeight: '700', color: '#0D47A1' }}>{periodLabel}</div>
                  {[1, 2, 3, 4, 5].map(day => {
                    const matchingSchedules = filteredByDivision.filter(
                      s => s.day_of_week === day && s.period === period
                    );
                    return (
                      <div key={day} style={{ padding: '0 5px', wordWrap: 'break-word', textAlign: 'left' }}>
                        {matchingSchedules.length > 0 ? (
                          matchingSchedules.map((s, i) => (
                            <div key={i} style={{ marginBottom: 6 }}>
                              <div style={{ fontWeight: '600', color: '#1565c0' }}>
                                {s.subject.name} ({s.grade_class.name})
                              </div>
                              <div style={{ fontSize: 12, color: '#333' }}>{s.teacher.full_name}</div>
                              <div style={{ fontSize: 11, color: '#999' }}>
                                {formatTo12Hour(s.start_time)} - {formatTo12Hour(s.end_time)}
                              </div>
                            </div>
                          ))
                        ) : (
                          <span style={{ color: '#aaa' }}>—</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Edit Dialog */}
        <Dialog open={!!editItem} onClose={() => setEditItem(null)}>
          <DialogTitle>Edit Schedule</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="dense">
              <InputLabel id="day-label">Day of Week</InputLabel>
              <Select
                labelId="day-label"
                name="day_of_week"
                label="Day of Week"
                value={editForm.day_of_week || ''}
                onChange={handleEditChange}
              >
                {dayOptions.map((day) => (
                  <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth margin="dense">
              <InputLabel id="period-label">Period</InputLabel>
              <Select
                labelId="period-label"
                name="period"
                label="Period"
                value={editForm.period || ''}
                onChange={handleEditChange}
              >
                {periodOptions.map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="start_time"
              label="Start Time"
              type="time"
              fullWidth
              value={editForm.start_time || ''}
              onChange={handleEditChange}
            />
            <TextField
              margin="dense"
              name="end_time"
              label="End Time"
              type="time"
              fullWidth
              value={editForm.end_time || ''}
              onChange={handleEditChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditItem(null)} disabled={loading}>Cancel</Button>
            <Button onClick={submitEdit} variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </div>
  );
};

export default SchedulePage;


